import type { Dictionary } from "./dictionary";

export const OVERVIEW_DEMO_STRINGS: Dictionary = {
  // ---- overview dashboard (#376) ----
  "overview.openQueue": { de: "Prüfliste öffnen", en: "Open review queue" },
  "overview.workTitle": { de: "Vorgänge & Bearbeitung", en: "Requests & processing" },
  "overview.incoming.processing": { de: "Wird bearbeitet", en: "Processing" },
  "overview.incoming.unseen_ready": { de: "Entwurf bereit", en: "Draft ready" },
  "overview.incoming.processing.count": { de: "Anfragen in Bearbeitung: {n}", en: "Requests processing: {n}" },
  "overview.incoming.unseen_ready.count": { de: "Aktuelle, vom Team noch nicht angesehene Entwürfe: {n}", en: "Current drafts not yet viewed by the team: {n}" },
  "overview.incoming.loading": { de: "Bearbeitungsstand wird geladen …", en: "Loading processing and draft counts …" },
  "overview.incoming.unavailable": { de: "Bearbeitungsstand nicht verfügbar", en: "Processing and draft counts unavailable" },
  "overview.workSubtitle": {
    de: "Neue Anfragen und offene Arbeit auf einen Blick.",
    en: "New enquiries and open work at a glance.",
  },
  "overview.views.aria": { de: "Weitere Listen", en: "Other lists" },
  "overview.statNewDescription": {
    de: "Neue, noch nicht bearbeitete Anfragen",
    en: "New enquiries not yet processed",
  },
  "overview.statNeedsActionDescription": {
    de: "Vorgänge, die eine Aktion von Ihnen benötigen",
    en: "Requests that need an action from you",
  },
  "overview.statWaitingCustomerDescription": {
    de: "Anfragen, bei denen wir auf eine Kundenantwort warten",
    en: "Enquiries where we are waiting for the customer",
  },
  "overview.statWaiting": { de: "Warten auf Sie", en: "Waiting for you" },
  "overview.statOpen": { de: "In Arbeit", en: "In progress" },
  "overview.statAnswered": { de: "Beantwortet", en: "Answered" },
  "overview.statToday": { de: "Heute eingegangen", en: "Received today" },
  "overview.volumeTitle": { de: "Anfragevolumen", en: "Enquiry volume" },
  "overview.volumeSub": { de: "letzte 14 Tage", en: "last 14 days" },
  "overview.legendAuto": { de: "automatisch bearbeitet", en: "handled automatically" },
  "overview.legendReview": { de: "manuell geprüft", en: "required manual review" },
  "overview.funnelTitle": { de: "Wo die Anfragen stehen", en: "Where enquiries stand" },
  "overview.funnelReceived": { de: "Eingegangen", en: "Received" },
  "overview.funnelInProgress": { de: "In Arbeit", en: "In progress" },
  "overview.funnelAnswered": { de: "Beantwortet", en: "Answered" },
  "overview.funnelClosed": { de: "Abgeschlossen", en: "Closed" },
  "overview.waitingTitle": { de: "Wartet auf Sie", en: "Waiting for you" },
  "overview.seeAll": { de: "Alle anzeigen", en: "See all" },
  "overview.reasonReview": { de: "Prüfung nötig", en: "Needs your review" },
  "overview.systemTitle": { de: "Systemstatus", en: "System status" },

  // ---- overview operational metrics (#811) ----
  // Every string here belongs to a number the server computed. None of them may be phrased so that
  // an absent value reads as a zero.
  "overview.metrics.title": { de: "Leistung", en: "Performance" },
  "overview.metrics.zoneNote": { de: "Alle Zeiträume in {zone}.", en: "All periods in {zone}." },
  "overview.metrics.periodAria": { de: "Zeitraum wählen", en: "Choose period" },
  "overview.metrics.period.day": { de: "Heute", en: "Today" },
  "overview.metrics.period.week": { de: "Diese Woche", en: "This week" },
  "overview.metrics.period.month": { de: "Dieser Monat", en: "This month" },
  "overview.metrics.period.custom": { de: "Zeitraum", en: "Custom" },
  "overview.metrics.newEnquiries": { de: "Neue Anfragen", en: "New enquiries" },
  "overview.metrics.selectedPeriod": { de: "Im gewählten Zeitraum", en: "In the selected period" },
  "overview.metrics.within24h": { de: "In 24 Stunden bearbeitet", en: "Processed within 24 hours" },
  "overview.metrics.within24hHint": {
    de: "{n} von {total} entschiedenen Anfragen",
    en: "{n} of {total} settled enquiries",
  },
  "overview.metrics.within24hUnavailable": {
    de: "Noch ist keine Anfrage entschieden.",
    en: "No enquiry is settled yet.",
  },
  "overview.metrics.delayed": { de: "Überfällig", en: "Delayed" },
  "overview.metrics.delayedHint": {
    de: "Seit mehr als 24 Stunden unbearbeitet",
    en: "Untouched for more than 24 hours",
  },
  "overview.metrics.averageGuests": { de: "Ø Gästezahl", en: "Avg. guest count" },
  "overview.metrics.averageGuestsHint": {
    de: "Aus {n} für den Durchschnitt berücksichtigten Anfragen",
    en: "From {n} enquiries contributing to the average",
  },
  "overview.metrics.averageGuestsUnavailable": {
    de: "Für diese Anfragen lässt sich keine durchschnittliche Gästezahl berechnen.",
    en: "An average guest count cannot be calculated for these enquiries.",
  },
  "overview.metrics.periodEmpty": {
    de: "Im gewählten Zeitraum ist keine Anfrage eingegangen.",
    en: "No enquiries arrived in the selected period.",
  },
  "overview.metrics.loading": { de: "Kennzahlen werden geladen", en: "Loading metrics" },
  "overview.metrics.unavailable": {
    de: "Diese Kennzahl ist derzeit nicht verfügbar.",
    en: "This metric is currently unavailable.",
  },
  "overview.metrics.error": {
    de: "Die Kennzahlen konnten nicht geladen werden.",
    en: "The metrics could not be loaded.",
  },
  // ---- overview team presence (#812) ----
  // Every string is bounded by what the server can know: a live Cockpit session, or not, or no
  // answer. None of them may be phrased as a claim about whether somebody is working.
  "overview.presence.title": { de: "Team", en: "Team" },
  "overview.presence.onlineOf": { de: "{n} von {total} angemeldet", en: "{n} of {total} signed in" },
  "overview.presence.state.online": { de: "Angemeldet", en: "Signed in" },
  "overview.presence.state.offline": { de: "Nicht angemeldet", en: "Not signed in" },
  "overview.presence.state.unknown": { de: "Unbekannt", en: "Unknown" },
  "overview.presence.loading": { de: "Team wird geladen…", en: "Loading the team…" },
  "overview.presence.headerEmpty": { de: "Nur Ihr Konto", en: "Your account only" },
  "overview.presence.headerUnavailable": {
    de: "Anmeldestatus unbekannt",
    en: "Sign-in status unknown",
  },
  "overview.presence.openStaff": { de: "Team anzeigen", en: "View team" },
  "overview.presence.openStaffArea": { de: "Zum Personalbereich", en: "Open Staff" },
  "overview.presence.empty": {
    de: "Zu diesem Betrieb gehört bisher nur Ihr Konto.",
    en: "This business has only your account so far.",
  },
  "overview.presence.unavailable": {
    de: "Der Anmeldestatus ist gerade nicht abrufbar. Wer angemeldet ist, bleibt bis auf Weiteres unbekannt.",
    en: "Sign-in status cannot be retrieved right now, so who is signed in stays unknown for the moment.",
  },
  "overview.metrics.rangeIncomplete": {
    de: "Bitte geben Sie beide Enden des Zeitraums an.",
    en: "Please give both ends of the period.",
  },
  // ---- overview operational footer (#979) ----
  // These labels name narrow server facts. They deliberately never claim global health or mailbox
  // synchronization, because neither has a matching source on the Overview.
  "overview.footer.performance": { de: "Leistung", en: "Performance" },
  "overview.footer.loading": { de: "wird geladen", en: "loading" },
  "overview.footer.unavailable": { de: "nicht verfügbar", en: "unavailable" },
  "overview.footer.openSystemStatus": { de: "Systemstatus öffnen", en: "Open System status" },

  // ---- workspace evidence links (#376; legend #438) ----
  "ws.linksTitle": { de: "was zusammengehört", en: "what connects" },
  "ws.linksClear": { de: "Markierungen aufheben", en: "Clear highlights" },
  "ws.legendAria": { de: "Farblegende", en: "Color legend" },

  // ---- admin redesign (#378) ----
  "shell.catalog": { de: "Katalog", en: "Catalog" },

  "catalog.title": { de: "Katalog", en: "Catalog" },
  // #2007: the grouped article list and the article dialog.
  "catalog.add": { de: "Artikel anlegen", en: "Add article" },
  "catalog.articles.title": { de: "Artikel", en: "Articles" },
  "catalog.summary.groups": { de: "Produktgruppen", en: "Product groups" },
  "catalog.search.placeholder": { de: "Artikel, Nummer oder Gruppe suchen…", en: "Search article, number or group…" },
  "catalog.empty.owner": { de: "Noch keine Artikel. Legen Sie einen Artikel an oder importieren Sie unten einen Katalog.", en: "No articles yet. Add an article or import a catalog below." },
  "catalog.empty.admin": { de: "Noch keine Artikel. Legen Sie den ersten Artikel an.", en: "No articles yet. Add the first article." },
  "catalog.group.none": { de: "Ohne Gruppe", en: "No group" },
  "catalog.group.count": { de: "{count} Artikel", en: "{count} articles" },
  "catalog.group.count.one": { de: "{count} Artikel", en: "{count} article" },
  "catalog.col.number": { de: "Nr.", en: "No." },
  "catalog.col.action": { de: "Bearbeiten", en: "Edit" },
  "catalog.flag.manual": { de: "Selbst angelegt", en: "Added by hand" },
  "catalog.edit": { de: "Artikel {name} bearbeiten", en: "Edit article {name}" },
  "catalog.form.editTitle": { de: "Artikel bearbeiten", en: "Edit article" },
  "catalog.form.name": { de: "Name", en: "Name" },
  "catalog.form.group": { de: "Produktgruppe", en: "Product group" },
  "catalog.form.groupHelper": { de: "Eine vorhandene Gruppe wählen oder eine neue eintragen. Name und Gruppe stehen danach fest.", en: "Choose an existing group or enter a new one. Name and group are fixed afterwards." },
  "catalog.form.priceInvalid": { de: "Bitte einen Betrag wie 12,50 eingeben.", en: "Enter an amount such as 12.50." },
  "catalog.form.vatInvalid": { de: "Bitte eine ganze Zahl von 0 bis 100 eingeben.", en: "Enter a whole number from 0 to 100." },
  "catalog.form.active": { de: "In Angeboten verwenden", en: "Use in proposals" },
  "catalog.form.importedHint": { de: "Name, Gruppe und Verfügbarkeit dieses Artikels kommen aus dem Import.", en: "This article's name, group and availability come from the import." },
  "catalog.form.stale": { de: "Der Artikel wurde inzwischen geändert. Schließen Sie den Dialog und öffnen Sie den Artikel erneut.", en: "This article has changed in the meantime. Close the dialog and open the article again." },
  "catalog.created": { de: "Artikel angelegt.", en: "Article added." },
  "catalog.saved": { de: "Artikel gespeichert.", en: "Article saved." },
  "staff.peopleCount": { de: "{n} Personen", en: "{n} people" },
  "staff.manage": { de: "Verwalten", en: "Manage" },
  "staff.platformManaged": { de: "Von der Plattform verwaltet", en: "Platform-managed" },
  "staff.you": { de: "Sie", en: "You" },
  "staff.inviteNote": { de: "Sie vergeben das Passwort selbst und geben es weiter. Operatoren können Preise, Regeln und Einstellungen nicht ändern.", en: "You set the password yourself and pass it on. Operators can never change prices, rules or settings." },
  "staff.roleOptionOperator": { de: "Operator — beantwortet Anfragen", en: "Operator — answers enquiries" },
  "staff.roleOptionAdmin": { de: "Admin — auch Team & Einstellungen", en: "Admin — also team & settings" },
  "staff.section.profile": { de: "Person", en: "Profile" },
  "staff.section.access": { de: "Rechte", en: "Access" },
  "staff.section.password": { de: "Passwort", en: "Password" },
  "staff.section.remove": { de: "Löschen", en: "Delete" },
  "staff.sectionsAria": { de: "Mitglied-Bereiche", en: "Member sections" },
  "staff.profileNote": { de: "Name und E-Mail ändert die Person selbst über ihr Konto.", en: "Name and email are changed by the person themselves via their account." },
  "staff.accessHint": { de: "Was diese Person darf. Jede Stufe schließt die darunter ein — geprüft wird bei jeder Aktion, nicht nur im Menü.", en: "What this person may do. Each level includes everything below it — the system checks this on every action, not just in the menu." },
  "staff.applyRole": { de: "Diese Stufe übernehmen", en: "Apply this level" },
  "staff.selfWarning": { de: "Das ist Ihr eigenes Konto. Eine niedrigere Stufe entzieht Ihnen den Zugang zu dieser Seite.", en: "This is your own account. Lowering your level will remove your access to this page." },
  "staff.roleSub.operator": { de: "Liest Anfragen und sendet Antworten. Kann Preise, Regeln und Einstellungen nicht ändern.", en: "Reads enquiries and sends replies. Cannot change prices, rules or settings." },
  "staff.roleSub.admin": { de: "Alles wie ein Operator, dazu Team und alle Einstellungen.", en: "Everything an operator may do, plus the team and all settings." },
  "staff.roleSub.owner": { de: "Alles wie ein Admin, dazu die Plattformseite mit allen Betrieben. Hier nicht vergebbar.", en: "Everything an admin may do, plus the platform page with every business. Not assignable from here." },
  "staff.pwHint": { de: "Sie vergeben das neue Passwort selbst und geben es weiter. Eine Zurücksetzen-E-Mail gibt es in diesem System nicht.", en: "You set the new password yourself and pass it on. There is no reset email in this system." },
  "staff.pwNote": { de: "Das alte Passwort gilt ab dem Speichern nicht mehr.", en: "The old password stops working the moment you save." },
  "staff.deleteFinal": { de: "Endgültig löschen", en: "Delete permanently" },
  "settings.section.system": { de: "Systemstatus", en: "System status" },
  "settings.section.signature": { de: "Signatur", en: "Signature" },
  "settings.section.logo": { de: "Logo", en: "Logo" },
  // #1432: Email holds every way enquiries arrive; Password left for the Account screen (#1431).
  "settings.section.email": { de: "E-Mail", en: "Email" },
  "settings.group.business": { de: "Dieser Betrieb", en: "This business" },
  "settings.group.platform": { de: "Plattform · Inhaber", en: "Platform · owner" },
  "settings.noBusiness": {
    de: "Wählen Sie einen Betrieb, um seine Einstellungen zu sehen.",
    en: "Choose a business to see its settings.",
  },
  "settings.sectionsAria": { de: "Einstellungs-Bereiche", en: "Settings sections" },
  "businesses.onboard": { de: "Betrieb aufnehmen", en: "Onboard a business" },
  "businesses.openAs": { de: "Als dieser Betrieb öffnen", en: "Open as this business" },
  "businesses.section.general": { de: "Stammdaten", en: "General" },
  "businesses.section.identity": { de: "Betrieb", en: "Business" },
  "businesses.section.access": { de: "Zugang", en: "Access" },
  "businesses.member": { de: "Mitglied", en: "Member" },
  "businesses.membership.joinTitle": { de: "Diesem Betrieb beitreten", en: "Join this business" },
  "businesses.membership.leaveTitle": { de: "Mitgliedschaft beenden", en: "Leave this business" },
  "businesses.membership.sub": { de: "Ihr bestehendes Inhaber-Konto wird als Personal geführt und kann Anfragen übernehmen.", en: "Your existing owner account is listed as staff and can take requests." },
  "businesses.membership.join": { de: "Beitreten", en: "Join" },
  "businesses.membership.leave": { de: "Verlassen", en: "Leave" },
  "businesses.membership.joined": { de: "Mitgliedschaft hinzugefügt", en: "Membership added" },
  "businesses.membership.left": { de: "Mitgliedschaft entfernt", en: "Membership removed" },
  "businesses.sectionsAria": { de: "Betrieb-Bereiche", en: "Business sections" },
  "businesses.pauseSending": { de: "Senden pausieren", en: "Pause sending" },
  "businesses.resumeSending": { de: "Senden fortsetzen", en: "Resume sending" },
  "businesses.pauseSub": { de: "Entwürfe werden weiter erstellt; gesendet wird erst nach dem Wiedereinschalten.", en: "Drafts keep being prepared; sending resumes only after you switch it back on." },
  "businesses.suspendSub": { de: "Alles hält an: kein Lesen, keine Entwürfe, kein Senden. Die Konten bleiben bestehen.", en: "Everything stops: no reading, no drafts, no sending. The team keeps their accounts." },
  "businesses.resumeSub": { de: "Der Betrieb arbeitet wieder normal.", en: "The business runs normally again." },
  "businesses.archiveFinal": { de: "Endgültig archivieren", en: "Archive permanently" },
  "businesses.archivedNote": { de: "Dieser Betrieb ist archiviert. Es sind keine Änderungen mehr möglich.", en: "This business is archived. No further changes are possible." },
  // #638: pipeline failure diagnostics + tenant health strip.
  "ws.analysis.diagnostic": { de: "Diagnose: {code}", en: "Diagnostic: {code}" },
  "ws.analysis.diagnosticCode.ai_timeout": { de: "Zeitüberschreitung des KI-Dienstes", en: "AI service timeout" },
  "ws.analysis.diagnosticCode.ai_unavailable": { de: "KI-Dienst nicht erreichbar", en: "AI service unreachable" },
  "ws.analysis.diagnosticCode.ai_provider_error": { de: "Fehler des KI-Anbieters", en: "AI provider error" },
  "ws.analysis.diagnosticCode.quota_exhausted": { de: "KI-Budget aufgebraucht", en: "AI budget exhausted" },
  "ws.analysis.diagnosticCode.feature_disabled": { de: "Funktion deaktiviert", en: "Feature disabled" },
  "ws.analysis.diagnosticCode.db_constraint": { de: "Datenbank-Konsistenzfehler", en: "Database constraint failure" },
  "ws.analysis.diagnosticCode.db_query": { de: "Datenbankabfrage fehlgeschlagen", en: "Database query failed" },
  "ws.analysis.diagnosticCode.contract_validation": { de: "Datenvertrag verletzt", en: "Contract validation failed" },
  "ws.analysis.diagnosticCode.send_not_eligible": { de: "Senden nicht zulässig", en: "Send not eligible" },
  "ws.analysis.diagnosticCode.unexpected": { de: "Unerwarteter Fehler", en: "Unexpected failure" },
  "health.failedRuns.one": { de: "{count} Analyse muss manuell erneut angestoßen werden", en: "{count} analysis requires a manual retry" },
  "health.failedRuns.other": { de: "{count} Analysen müssen manuell erneut angestoßen werden", en: "{count} analyses require a manual retry" },
  "health.failedTitle": { de: "Anfragen, deren Analyse ohne erneuten Anstoß nicht weiterläuft", en: "Requests whose analysis will not continue without a manual retry" },
  // #677: per-business test injection, offered on the inbox while `test_injection` is on.
  "testMail.badge": { de: "Testmodus", en: "Test mode" },
  "testMail.title": { de: "Test-E-Mail einspielen", en: "Inject a test email" },
  "testMail.show": { de: "Öffnen", en: "Open" },
  "testMail.hide": { de: "Schließen", en: "Close" },
  "testMail.hint": {
    de: "Die gewählte Anfrage trifft wie echte Post in {mailbox} ein und durchläuft dieselbe Verarbeitung.",
    en: "The chosen enquiry arrives in {mailbox} like real mail and runs through the same processing.",
  },
  "testMail.noMailbox": {
    de: "Für diesen Betrieb ist kein Postfach hinterlegt — ohne Postfach lässt sich keine Test-E-Mail einspielen.",
    en: "This business has no mailbox configured — a test email cannot be injected without one.",
  },
  "testMail.category": { de: "Kategorie", en: "Category" },
  "testMail.categoryAll": { de: "Alle Kategorien", en: "All categories" },
  "testMail.scenario": { de: "Szenario", en: "Scenario" },
  "testMail.expectation": { de: "Erwartet", en: "Expected" },
  // #767: a repeat injection of the same scenario joins the first one's conversation, so the
  // list gains no row and the operator has nothing new to click. This is that way back.
  "testMail.lastInjected": {
    de: "Zuletzt eingespielt: „{scenario}“.",
    en: "Last injected: “{scenario}”.",
  },
  "testMail.openLast": { de: "Vorgang öffnen", en: "Open the request" },
  "testMail.send": { de: "Einspielen", en: "Inject" },
  "testMail.random": { de: "Zufällig einspielen", en: "Inject a random one" },
  "testMail.sending": { de: "Wird eingespielt…", en: "Injecting…" },
  "testMail.done": { de: "Test-E-Mail eingegangen", en: "Test email received" },
  "testMail.doneBody": {
    de: "„{scenario}“ — erwartet: {outcome}",
    en: "“{scenario}” — expected: {outcome}",
  },
  "testMail.replyWarning": {
    de: "Absender sind reservierte Testadressen: Eine freigegebene Antwort wird bewusst nicht zugestellt.",
    en: "Senders are reserved test addresses: an approved reply is deliberately not delivered.",
  },
  "testMail.rowBadge": { de: "Test", en: "Test" },
  "testMail.rowBadgeTitle": {
    de: "Eingespielte Test-E-Mail — keine echte Kundenanfrage",
    en: "Injected test email — not a real customer enquiry",
  },
};
